import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { bootstrapAuth } from './lib/api';
import { useApp } from './state/store';
import './styles/globals.css';

// Register Service Worker for push notifications (timbre rings)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Append a build-time version query to the SW URL so each deploy
    // forces the browser to treat it as a new SW (different URL = different
    // SW per spec). Without this, iOS Safari can keep an old SW registered
    // across deploys because the /sw.js URL never changes and the byte
    // comparison happens at unpredictable intervals (24h+ on iOS).
    // Bump SW_VERSION whenever sw.js changes meaningfully.
    const SW_VERSION = '2026-06-29-v1-auto-update';
    navigator.serviceWorker
      .register(`/sw.js?v=${SW_VERSION}`)
      .then((registration) => {
        if (import.meta.env.DEV) {
          console.log('[PWA] SW registered:', registration.scope, 'v=', SW_VERSION);
        }

        // Auto-update: when a new SW is found and the previous one is already
        // controlling this page, the new SW will install → activate (because
        // sw.js calls skipWaiting + clients.claim). We listen for both the
        // `updatefound` lifecycle and an explicit `NEW_VERSION_AVAILABLE`
        // postMessage from the SW (more reliable across browsers, especially
        // when the SW activates via clients.claim instead of a navigation).
        const onUpdateReady = () => {
          // Reload so the page picks up the new bundle. We only reload if
          // there's an existing controller — otherwise this is the very first
          // install and the fresh page is already the latest code.
          if (navigator.serviceWorker.controller) {
            window.location.reload();
          }
        };

        if (registration.waiting) {
          // A new SW is already installed and waiting to activate.
          onUpdateReady();
        }
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              onUpdateReady();
            }
          });
        });
      })
      .catch((error) => {
        console.error('[PWA] SW registration failed:', error);
      });

    // Explicit message from SW (postMessage in sw.js activate handler).
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NEW_VERSION_AVAILABLE') {
        if (navigator.serviceWorker.controller) {
          window.location.reload();
        }
      }
    });
  });
}

// Boot-time auth refresh: if the access token expired while the PWA was
// closed but the refresh token is still valid, swap in a fresh access
// token before the user lands on a screen. Runs once, silently — failures
// just leave the store in its initial (possibly unauthenticated) state.
void bootstrapAuth()
  .then((result) => {
    useApp.getState().hydrateAuth({
      isAuthenticated: result.authenticated,
      user: result.user,
      community: result.community,
    });
  })
  .catch(() => {
    // Bootstrap failures are non-fatal; the synchronous store init already
    // did its best with the cached token.
  });

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
